import { tr } from '@repo/locale';
import { type TestId } from '@repo/test-id';
import { useRef, useState, type ChangeEvent, type FocusEvent } from 'react';
import { MINUTES_IN_HOUR } from '@repo/date-time';

import { sanitizeDigits } from '@shared/utils/codeInput';
import { parseInputValue, toDurationMinutes } from '@shared/utils/timeContractCalculations';

interface Props {
  parkDuration: number;
  onChange: (durationMinutes: number) => void;
  testId: TestId;
}

export default function TimeContractDurationInput({ parkDuration, onChange, testId }: Props) {
  const hours = Math.floor(parkDuration / MINUTES_IN_HOUR);
  const minutes = parkDuration % MINUTES_IN_HOUR;

  const [hoursInput, setHoursInput] = useState<string>(String(hours));
  const [minutesInput, setMinutesInput] = useState<string>(String(minutes));

  const hoursInputRef = useRef<HTMLInputElement>(null);
  const minutesInputRef = useRef<HTMLInputElement>(null);

  const lastSyncedHoursRef = useRef<number>(hours);
  if (lastSyncedHoursRef.current !== hours) {
    lastSyncedHoursRef.current = hours;
    if (parseInputValue(hoursInput) !== hours) {
      setHoursInput(String(hours));
    }
  }

  const lastSyncedMinutesRef = useRef<number>(minutes);
  if (lastSyncedMinutesRef.current !== minutes) {
    lastSyncedMinutesRef.current = minutes;
    if (parseInputValue(minutesInput) !== minutes) {
      setMinutesInput(String(minutes));
    }
  }

  const handleHoursChange = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = sanitizeDigits(event.target.value);
    setHoursInput(raw);

    onChange(toDurationMinutes(parseInputValue(raw), parseInputValue(minutesInput)));
  };

  const handleMinutesChange = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = sanitizeDigits(event.target.value);
    setMinutesInput(raw);

    onChange(toDurationMinutes(parseInputValue(hoursInput), parseInputValue(raw)));
  };

  const handleHoursBlur = () => {
    setHoursInput(String(hours));
  };

  const handleMinutesBlur = () => {
    setMinutesInput(String(minutes));
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    event.target.select();
  };

  const handleHoursBoxClick = () => {
    hoursInputRef.current?.focus();
  };

  const handleMinutesBoxClick = () => {
    minutesInputRef.current?.focus();
  };

  return (
    <div className="flex items-center justify-center">
      <div
        onClick={handleHoursBoxClick}
        className="bg-secondary-soft border-stroke-base inline-flex h-16 min-w-32 flex-col items-center justify-center rounded-tl-xl rounded-bl-xl border px-5 py-1.5"
      >
        <div className="text-heading flex items-center justify-start text-4xl leading-13.5 font-medium">
          <input
            ref={hoursInputRef}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={2}
            value={hoursInput}
            onChange={handleHoursChange}
            onFocus={handleFocus}
            onBlur={handleHoursBlur}
            aria-label={tr('time.hour_shortcut')}
            data-testid={testId.append('hours')}
            className="w-11 bg-transparent text-right outline-none"
          />
          {tr('time.hour_shortcut')}
        </div>
      </div>
      <div
        onClick={handleMinutesBoxClick}
        className="bg-secondary-soft border-stroke-base inline-flex h-16 min-w-40 flex-col items-center justify-center rounded-tr-xl rounded-br-xl border px-5 py-1.5"
      >
        <div className="text-heading flex items-center justify-start text-4xl leading-13.5 font-medium">
          <input
            ref={minutesInputRef}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={2}
            value={minutesInput}
            onChange={handleMinutesChange}
            onFocus={handleFocus}
            onBlur={handleMinutesBlur}
            aria-label={tr('time.minute_shortcut')}
            data-testid={testId.append('minutes')}
            className="w-11 bg-transparent text-right outline-none"
          />
          {tr('time.minute_shortcut')}
        </div>
      </div>
    </div>
  );
}
